import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

const useMagnetboardStore = createWithEqualityFn((set, get) => ({
  // Schedule data
  currentSchedule: {},
  otherSchedulesVisible: false,
  editingScheduleName: false,
  currentScheduleVersionID: null,
  currentViewType: null,
  scheduleType: null,
  dayOptions: [],
  currentDay: '1',
  periodType: null,
  currentView: 'teacher',
  displaySchedule: [],
  periodsToShow: [],
  displayCreated: false,

  // Conflicts
  matchPercent: 0,
  matchPercentLoading: true,
  conflicts: [],
  conflictsLoading: true,
  conflictDataKey: null,
  conflictCalculationKey: null,
  conflictDataRevision: 0,
  hasCompleteConflictData: false,
  completeConflictDataContext: null,

  // Loading states
  isSaving: false,
  isLoading: true,
  isLockSectionLoading: false,
  isLockSectionIds: [],

  // Filters
  filterIDs: [],
  filtered: false,
  singletons_filtered: false,
  doubletons_filtered: false,
  labs_only_filtered: false,
  non_labs_only_filtered: false,
  no_classroom_filtered: false,
  has_classroom_filtered: false,
  searchValue: '',
  filteredData: [],
  filterOptions: {},

  // Actions
  setCurrentSchedule: (schedule) => set({ currentSchedule: schedule }),
  setOtherSchedulesVisible: (visible) => set({ otherSchedulesVisible: visible }),
  toggleOtherScheduleOptions: () => set((state) => ({ 
    otherSchedulesVisible: !state.otherSchedulesVisible 
  })),
  setEditingScheduleName: (editing) => set({ editingScheduleName: editing }),
  setIsSaving: (saving) => set({ isSaving: saving }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setIsLockSectionLoading: (loading) => set({ isLockSectionLoading: loading }),
  setIsLockSectionIds: (sectionIds) => set({ isLockSectionIds: sectionIds }),
  setCurrentScheduleVersionID: (id) => set({ currentScheduleVersionID: id }),
  setCurrentViewType: (viewType) => set({ currentViewType: viewType }),
  setScheduleType: (type) => set({ scheduleType: type }),
  setDayOptions: (options) => set({ dayOptions: options }),
  setCurrentDay: (day) => set({ currentDay: day }),
  setPeriodType: (type) => set({ periodType: type }),
  setCurrentView: (view) => set({ currentView: view }),
  setDisplaySchedule: (schedule) => set({ displaySchedule: schedule }),
  setPeriodsToShow: (periods) => set({ periodsToShow: periods }),
  setDisplayCreated: (created) => set({ displayCreated: created }),

  // Conflicts
  setMatchPercent: (percent) => set({ matchPercent: percent }),
  setMatchPercentLoading: (loading) => set({ matchPercentLoading: loading }),
  setConflicts: (conflicts) => set({ conflicts: conflicts }),
  setConflictsLoading: (loading) => set({ conflictsLoading: loading }),
  setConflictDataKey: (key) => set({ conflictDataKey: key }),
  setConflictCalculationKey: (key) => set({ conflictCalculationKey: key }),
  markConflictDataFresh: (school_id, schedule_version_id, options = {}) => set((state) => {
    if (!school_id || !schedule_version_id) return {};

    const complete = options.complete === true;
    const conflict_data_context = `${school_id}:${schedule_version_id}`;
    if (!complete && (!state.hasCompleteConflictData || state.completeConflictDataContext !== conflict_data_context)) return {};

    return {
      conflictDataKey: `${conflict_data_context}:${state.conflictDataRevision}`,
      conflictCalculationKey: null,
      hasCompleteConflictData: complete ? true : state.hasCompleteConflictData,
      completeConflictDataContext: complete ? conflict_data_context : state.completeConflictDataContext,
    };
  }),
  invalidateConflictData: () => set((state) => ({
    conflictDataKey: null,
    conflictCalculationKey: null,
    conflictDataRevision: state.conflictDataRevision + 1,
  })),

  // Filters
  setFilterIDs: (ids) => set({ filterIDs: ids }),
  setFiltered: (filtered) => set({ filtered: filtered }),
  setSingletonFiltered: (filtered) => set({ singletons_filtered: filtered }),
  setDoubletonFiltered: (filtered) => set({ doubletons_filtered: filtered }),
  setLabsOnlyFiltered: (filtered) => set({ labs_only_filtered: filtered }),
  setNonLabsOnlyFiltered: (filtered) => set({ non_labs_only_filtered: filtered }),
  setNoClassroomFiltered: (filtered) => set({ no_classroom_filtered: filtered }),
  setHasClassroomFiltered: (filtered) => set({ has_classroom_filtered: filtered }),
  setSearchValue: (value) => set({ searchValue: value }),
  setFilteredData: (data) => set({ filteredData: data }),
  setFilterOptions: (options) => set({ filterOptions: options }),
  clearFilters: () => set((state) => {
    const cleared_filter_options = Object.keys(state.filterOptions || {}).reduce((results, filter_title) => {
      const filter_options = Array.isArray(state.filterOptions[filter_title]) ? state.filterOptions[filter_title] : [];
      results[filter_title] = filter_options.map((filter_option) => {
        const cleared_option = {
          ...filter_option,
          selected: false,
        };

        if(`${filter_option.value}` === 'student_range') {
          cleared_option.start_student_id = null;
          cleared_option.end_student_id = null;
          cleared_option.display = 'Student Range';
        }

        return cleared_option;
      });
      return results;
    }, {});

    return {
      filterIDs: [],
      filtered: false,
      singletons_filtered: false,
      doubletons_filtered: false,
      labs_only_filtered: false,
      non_labs_only_filtered: false,
      no_classroom_filtered: false,
      has_classroom_filtered: false,
      searchValue: '',
      filteredData: [],
      filterOptions: cleared_filter_options,
    };
  }),
  resetStore: () =>
    set({
      currentSchedule: {},
      otherSchedulesVisible: false,
      editingScheduleName: false,
      currentScheduleVersionID: null,
      currentViewType: null,
      scheduleType: null,
      dayOptions: [],
      currentDay: '1',
      periodType: null,
      currentView: 'teacher',
      displaySchedule: [],
      periodsToShow: [],
      displayCreated: false,
      matchPercent: 0,
      matchPercentLoading: true,
      conflicts: [],
      conflictsLoading: true,
      conflictDataKey: null,
      conflictCalculationKey: null,
      conflictDataRevision: 0,
      hasCompleteConflictData: false,
      completeConflictDataContext: null,
      isSaving: false,
      isLoading: true,
      isLockSectionLoading: false,
      isLockSectionIds: [],
      filterIDs: [],
      filtered: false,
      singletons_filtered: false,
      doubletons_filtered: false,
      labs_only_filtered: false,
      non_labs_only_filtered: false,
      no_classroom_filtered: false,
      has_classroom_filtered: false,
      searchValue: '',
      filteredData: [],
      filterOptions: {},
    }),

  // Bulk update action
  updateStore: (data) => set((state) => ({ ...state, ...data })),

  // Main initialization action
  initializeSchedule: (scheduleInfo, schoolInfo, scheduleStructureData = null) => {
    const state = get();
    if (!scheduleInfo?.schedules || !schoolInfo?.current_schedule_version_id) return;
    
    const schedule_index = scheduleInfo.schedules.findIndex(
      (schedule) => schedule.schedule_version_id === schoolInfo.current_schedule_version_id
    );
    
    if (schedule_index === -1) return;
    
    const schedule = scheduleInfo.schedules[schedule_index];
    const structure_days = Object.keys(scheduleStructureData?.schedule_structure || {})
      .map((day) => parseInt(day, 10))
      .filter((day) => Number.isInteger(day))
      .sort((a, b) => a - b)
      .map((day) => `${day}`);
    const fallback_num_days = parseInt(scheduleInfo.num_days_in_cycle || 1, 10);
    const day_values = structure_days.length > 0
      ? structure_days
      : Array.from({ length: Number.isInteger(fallback_num_days) && fallback_num_days > 0 ? fallback_num_days : 1 }, (_, i) => `${i + 1}`);
    const day_options = day_values.map((day) => ({ value: day, display: `Day ${day}` }));
    
    const updates = {
      currentScheduleVersionID: schedule.schedule_version_id,
      currentSchedule: { ...schedule },
      scheduleType: scheduleInfo.schedule_type,
      dayOptions: day_options
    };
    
    // Se currentViewType ainda não foi definido
    if (!state.currentViewType) {
      updates.currentViewType = scheduleInfo.schedule_type === "daily" ? "day" : "period";
    }
    
    set(state => ({
      ...state,
      ...updates
    }));
  },

  updatePeriodType: () => {
      const { scheduleType, currentViewType, currentDay, dayOptions } = get();
      if (!scheduleType || !currentViewType) return;
      
      const period_type = (scheduleType === 'daily' || currentViewType === 'day') 
        ? 'schedule_periods' 
        : ((scheduleType === 'ab' || scheduleType === 'ab_skinny') 
          ? 'day_course_periods' 
          : 'course_periods');

      let new_current_day = null;
      if (period_type === "schedule_periods") {
        const valid_day_values = dayOptions.map((option) => `${option.value}`);
        const stored_day = sessionStorage.getItem('current_day');
        const preferred_day = valid_day_values.includes(`${currentDay}`)
          ? `${currentDay}`
          : (stored_day && valid_day_values.includes(`${stored_day}`) ? `${stored_day}` : null);
        new_current_day = preferred_day || valid_day_values[0] || '1';
      }
      
      set(state => ({
        ...state,
        periodType: period_type,
        currentDay: new_current_day
      }));
      
      sessionStorage.setItem('current_view_type', currentViewType);
      if (new_current_day) sessionStorage.setItem('current_day', new_current_day);
    },

  loadFromSessionStorage: () => {
    const current_day = sessionStorage.getItem('current_day');
    const current_view = sessionStorage.getItem('current_view');
    const current_view_type = sessionStorage.getItem('current_view_type');

    const updates = {};
    if (current_day) updates.currentDay = current_day;
    if (current_view) updates.currentView = current_view;
    if (current_view_type) updates.currentViewType = current_view_type;
    
    if (Object.keys(updates).length > 0) {
      set(updates);
    }
  },
  changeCurrentViewType: (new_view_type) => {
    set((state) => ({
      ...state,
      isLoading: true,
      currentViewType: new_view_type
    }));
    sessionStorage.setItem('current_view_type', new_view_type);
  },
	changeCurrentDay: (new_day) => {
    set(state => ({
      ...state,
      currentDay: new_day
    }));
		sessionStorage.setItem('current_day', new_day);
	},
	changeCurrentView: (new_view) => {
    set(state => ({
      ...state,
      isLoading: true,
      filterIDs: [],
      filtered: false,
      singletons_filtered: false,
      doubletons_filtered: false,
      labs_only_filtered: false,
      non_labs_only_filtered: false,
      no_classroom_filtered: false,
      has_classroom_filtered: false,
      searchValue: '',
      filteredData: [],
      filterOptions: {},
      displayCreated: false,
      currentView: new_view
    }));
		sessionStorage.setItem('current_view', new_view);
	}
}), {
  name: 'magnetboard-store', // Nome no devtools
});

export default useMagnetboardStore;

// Seletores específicos para performance (opcional)
export const useLoadingData = () => useMagnetboardStore((state) => ({
  isSaving: state.isSaving,
  isLoading: state.isLoading,
  isLockSectionLoading: state.isLockSectionLoading,
  isLockSectionIds: state.isLockSectionIds
}), shallow);
export const useLoadingActions = () => useMagnetboardStore((state) => ({
  setIsSaving: state.setIsSaving,
  setIsLoading: state.setIsLoading,
  setIsLockSectionLoading: state.setIsLockSectionLoading,
  setIsLockSectionIds: state.setIsLockSectionIds
}), shallow);

export const useScheduleData = () => useMagnetboardStore((state) => ({
  currentSchedule: state.currentSchedule,
  editingScheduleName: state.editingScheduleName,
  currentScheduleVersionID: state.currentScheduleVersionID,
  otherSchedulesVisible: state.otherSchedulesVisible,
  displaySchedule: state.displaySchedule,
  displayCreated: state.displayCreated,
  scheduleType: state.scheduleType,
  dayOptions: state.dayOptions,
}), shallow);
export const useScheduleActions = () => useMagnetboardStore((state) => ({
  setCurrentSchedule: state.setCurrentSchedule,
  setEditingScheduleName: state.setEditingScheduleName,
  toggleOtherScheduleOptions: state.toggleOtherScheduleOptions,
  setOtherSchedulesVisible: state.setOtherSchedulesVisible,
  setIsSaving: state.setIsSaving,
  setCurrentScheduleVersionID: state.setCurrentScheduleVersionID,
  updateStore: state.updateStore,
  // ✅ Actions compostas
  initializeSchedule: state.initializeSchedule,
  updatePeriodType: state.updatePeriodType,
  loadFromSessionStorage: state.loadFromSessionStorage,
  setDisplaySchedule: state.setDisplaySchedule,
  setDisplayCreated: state.setDisplayCreated,
  resetStore: state.resetStore,
}), shallow);

export const useConflictData = () => useMagnetboardStore((state) => ({
  matchPercent: state.matchPercent,
  matchPercentLoading: state.matchPercentLoading,
  conflicts: state.conflicts,
  conflictsLoading: state.conflictsLoading,
  conflictDataKey: state.conflictDataKey,
  conflictCalculationKey: state.conflictCalculationKey,
  conflictDataRevision: state.conflictDataRevision,
  hasCompleteConflictData: state.hasCompleteConflictData
}), shallow);
export const useConflictActions = () => useMagnetboardStore((state) => ({
  setMatchPercent: state.setMatchPercent,
  setMatchPercentLoading: state.setMatchPercentLoading,
  setConflicts: state.setConflicts,
  setConflictsLoading: state.setConflictsLoading,
  setConflictDataKey: state.setConflictDataKey,
  setConflictCalculationKey: state.setConflictCalculationKey,
  markConflictDataFresh: state.markConflictDataFresh,
  invalidateConflictData: state.invalidateConflictData
}), shallow);

export const useViewData = () => useMagnetboardStore((state) => ({
  currentView: state.currentView,
  currentViewType: state.currentViewType,
  currentDay: state.currentDay,
  periodType: state.periodType,
  periodsToShow: state.periodsToShow,
}), shallow);
export const useViewActions = () => useMagnetboardStore((state) => ({
  setCurrentView: state.setCurrentView,
  setCurrentViewType: state.setCurrentViewType,
  setCurrentDay: state.setCurrentDay,
  setPeriodType: state.setPeriodType,
  setScheduleType: state.setScheduleType,
  setDayOptions: state.setDayOptions,
  setPeriodsToShow: state.setPeriodsToShow,
  changeCurrentDay: state.changeCurrentDay,
  changeCurrentView: state.changeCurrentView,
  changeCurrentViewType: state.changeCurrentViewType
}), shallow);

export const useFilterData = () => useMagnetboardStore((state) => ({
  filterIDs: state.filterIDs,
  filtered: state.filtered,
  singletons_filtered: state.singletons_filtered,
  doubletons_filtered: state.doubletons_filtered,
  labs_only_filtered: state.labs_only_filtered,
  non_labs_only_filtered: state.non_labs_only_filtered,
  no_classroom_filtered: state.no_classroom_filtered,
  has_classroom_filtered: state.has_classroom_filtered,
  searchValue: state.searchValue,
  filteredData: state.filteredData,
  filterOptions: state.filterOptions,
}), shallow);
export const useFilterActions = () => useMagnetboardStore((state) => ({
  setFilterIDs: state.setFilterIDs,
  setFiltered: state.setFiltered,
  setSingletonFiltered: state.setSingletonFiltered,
  setDoubletonFiltered: state.setDoubletonFiltered,
  setLabsOnlyFiltered: state.setLabsOnlyFiltered,
  setNonLabsOnlyFiltered: state.setNonLabsOnlyFiltered,
  setNoClassroomFiltered: state.setNoClassroomFiltered,
  setHasClassroomFiltered: state.setHasClassroomFiltered,
  setSearchValue: state.setSearchValue,
  setFilteredData: state.setFilteredData,
  setFilterOptions: state.setFilterOptions,
  clearFilters: state.clearFilters,
}), shallow);
